import React, { useState, useRef, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";
import { format } from "date-fns";
import openSocket from "../../services/socket-io";


import Popover from "@material-ui/core/Popover";
import IconButton from "@material-ui/core/IconButton";
import Button from "@material-ui/core/Button";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import { makeStyles } from "@material-ui/core/styles";
import Badge from "@material-ui/core/Badge";
import ChatIcon from "@material-ui/icons/Chat";

import TicketListItem from "../TicketListItem";
import { i18n } from "../../translate/i18n";
import useTickets from "../../hooks/useTickets";
import alertSound from "../../assets/sound.mp3";
import { AuthContext } from "../../context/Auth/AuthContext";
import { registerPushForCurrentUser } from "../../services/webPush";

const useStyles = makeStyles(theme => ({
	tabContainer: {
		overflowY: "auto",
		maxHeight: 350,
		...theme.scrollbarStyles,
	},
	popoverPaper: {
		width: "100%",
		maxWidth: 350,
		marginLeft: theme.spacing(2),
		marginRight: theme.spacing(1),
		[theme.breakpoints.down("sm")]: {
			maxWidth: 270,
		},
	},
	noShadow: {
		boxShadow: "none !important",
	},
	iconButton: {
		color: theme.palette.common.white,
	},
}));

const NotificationsPopOver = () => {
	const classes = useStyles();

	const history = useHistory();
	const { user } = useContext(AuthContext);
	const ticketIdUrl = +history.location.pathname.split("/")[2];
	const ticketIdRef = useRef(ticketIdUrl);
	const anchorEl = useRef();
	const [isOpen, setIsOpen] = useState(false);
	const [notifications, setNotifications] = useState([]);
        const [pushBusy, setPushBusy] = useState(false);
        const [pushEnabled, setPushEnabled] = useState(false);
        const [pushInfo, setPushInfo] = useState(
            "Activa las notificaciones para recibir avisos en Android."
        );

	const [, setDesktopNotifications] = useState([]);

        useEffect(() => {
            if (
                !isOpen ||
                !user?.id ||
                !("Notification" in window) ||
                Notification.permission !== "granted"
            ) {
                setPushEnabled(false);
                return undefined;
            }

            let active = true;

            registerPushForCurrentUser(user.id)
                .then(confirmed => {
                    if (!active) return;
                    setPushEnabled(confirmed === true);
                    if (confirmed === true) {
                        setPushInfo("Dispositivo registrado correctamente.");
                    }
                })
                .catch(() => {
                    if (active) setPushEnabled(false);
                });

            return () => { active = false; };
        }, [isOpen, user?.id]);


	const { tickets } = useTickets({ withUnreadMessages: "true" });
	
	const soundAlertRef = useRef();

	const historyRef = useRef(history);

	useEffect(() => {
                const audio = new Audio(alertSound);
                audio.preload = "auto";
                let active = true;
                let warned = false;

                const reportAudioError = error => {
                        if (!active || error?.name === "AbortError") return;
                        if (!warned) {
                                warned = true;
                                console.warn(
                                        "Sonido de notificacion no disponible:",
                                        error?.name || "Error de reproduccion"
                                );
                        }
                };

                soundAlertRef.current = () => {
                        if (!active) return;
                        try {
                                audio.currentTime = 0;
                                const pending = audio.play();
                                if (pending && typeof pending.catch === "function") {
                                        pending.catch(reportAudioError);
                                }
                        } catch (error) {
                                reportAudioError(error);
                        }
                };

                return () => {
                        active = false;
                        soundAlertRef.current = () => {};
                        audio.pause();
                        audio.removeAttribute("src");
                        audio.load();
                };
        }, []);

	useEffect(() => {
		setNotifications(tickets);
	}, [tickets]);

	useEffect(() => {
		ticketIdRef.current = ticketIdUrl;
	}, [ticketIdUrl]);

	useEffect(() => {
		const socket = openSocket();

		socket.on("connect", () => socket.emit("joinNotification"));

		socket.on("ticket", data => {
			if (data.action === "updateUnread" || data.action === "delete") {
				setNotifications(prevState => {
					const ticketIndex = prevState.findIndex(t => t.id === data.ticketId);
					if (ticketIndex !== -1) {
						prevState.splice(ticketIndex, 1);
						return [...prevState];
					}
					return prevState;
				});

				setDesktopNotifications(prevState => {
					const notfiticationIndex = prevState.findIndex(
						n => n.tag === String(data.ticketId)
					);
					if (notfiticationIndex !== -1) {
						prevState[notfiticationIndex].close();
						prevState.splice(notfiticationIndex, 1);
						return [...prevState];
					}
					return prevState;
				});
			}
		});

		socket.on("appMessage", data => {
			if (
				data.action === "create" &&
				!data.message.read &&
				(data.ticket.userId === user?.id || !data.ticket.userId)
			) {
				setNotifications(prevState => {
					const ticketIndex = prevState.findIndex(t => t.id === data.ticket.id);
					if (ticketIndex !== -1) {
						prevState[ticketIndex] = data.ticket;
						return [...prevState];
					}
					return [data.ticket, ...prevState];
				});

				const shouldNotNotificate =
					(data.message.ticketId === ticketIdRef.current &&
						document.visibilityState === "visible") ||
					(data.ticket.userId && data.ticket.userId !== user?.id) ||
					data.ticket.isGroup;

				if (shouldNotNotificate) return;

				handleNotifications(data);
			}
		});

		return () => {
			socket.disconnect();
		};
	}, [user]);


	useEffect(() => {
	  if (!("serviceWorker" in navigator)) return;
	  navigator.serviceWorker.register("/mandaexpress-sw.js", { scope: "/" })
	    .catch(err => console.warn("Service worker:", err));
	}, []);

	const handleNotifications = data => {
		const { message, contact, ticket } = data;

		const options = {
			body: `${message.body} - ${format(new Date(), "HH:mm")}`,
			icon: contact.profilePicUrl,
			tag: ticket.id,
			renotify: true,
		};


		if (/Android/i.test(navigator.userAgent)) {
		  // No avisar al repartidor sobre tickets no asignados a el.
		  if (user?.profile === "repartidor" &&
		      Number(ticket.userId) !== Number(user?.id)) return;

		  const id = Number(ticket.id);

		  if (Number.isSafeInteger(id) && id > 0 &&
		      "Notification" in window &&
		      Notification.permission === "granted" &&
		      "serviceWorker" in navigator) {
		    navigator.serviceWorker.ready
		      .then(reg => reg.showNotification("MandaExpress", {
		        body: "Tienes un nuevo mensaje.",
		        icon: "/mandaexpress-192.png",
		        badge: "/mandaexpress-192.png",
		        tag: `mandaexpress-${id}`,
		        renotify: true,
		        data: { path: `/tickets/${id}` }
		      }))
		      .catch(err => console.warn("Notificacion Android:", err));
		  }

		  try {
		    soundAlertRef.current?.();
		  } catch (err) {
		    console.warn("Sonido no disponible:", err);
		  }
		  return;
		}

		const notification = new Notification(
			`${i18n.t("tickets.notification.message")} ${contact.name}`,
			options
		);

		notification.onclick = e => {
			e.preventDefault();
			window.focus();
			historyRef.current.push(`/tickets/${ticket.id}`);
		};

		setDesktopNotifications(prevState => {
			const notfiticationIndex = prevState.findIndex(
				n => n.tag === notification.tag
			);
			if (notfiticationIndex !== -1) {
				prevState[notfiticationIndex] = notification;
				return [...prevState];
			}
			return [notification, ...prevState];
		});

		soundAlertRef.current();
	};

	
        const handleEnablePush = async () => {
            if (pushBusy) return;

            setPushBusy(true);
            setPushInfo("Comprobando notificaciones...");

            try {
                if (
                    !window.isSecureContext ||
                    !("Notification" in window) ||
                    !("serviceWorker" in navigator) ||
                    !("PushManager" in window)
                ) {
                    setPushInfo(
                        "Este navegador no admite Web Push. Prueba Chrome en Android."
                    );
                    return;
                }

                if (!user?.id) {
                    setPushInfo("Primero inicia sesion.");
                    return;
                }

                let permission = Notification.permission;

                if (permission === "default") {
                    permission = await Notification.requestPermission();
                }

                if (permission !== "granted") {
                    setPushInfo(
                        "Permiso bloqueado. Habilitalo en los ajustes del navegador."
                    );
                    return;
                }

                setPushInfo("Registrando dispositivo...");

                const confirmed = await registerPushForCurrentUser(user.id);
                setPushEnabled(confirmed === true);
                setPushInfo(
                    confirmed === true
                        ? "Dispositivo registrado correctamente."
                        : "No se pudo confirmar el registro."
                );
            } catch (error) {
                setPushEnabled(false);
                const status = error?.response?.status;

                setPushInfo(
                    status
                        ? "Error del servidor: HTTP " + status
                        : "Error de registro: " + (error?.name || "desconocido")
                );
            } finally {
                setPushBusy(false);
            }
        };

const handleClick = () => {
		setIsOpen(prevState => !prevState);
	};

	const handleClickAway = () => {
		setIsOpen(false);
	};

	const NotificationTicket = ({ children }) => {
		return <div onClick={handleClickAway}>{children}</div>;
	};

	return (
		<>
			<IconButton
				onClick={handleClick}
				ref={anchorEl}
				aria-label="Open Notifications"
				className={classes.iconButton}
			>
				<Badge badgeContent={notifications.length} color="error" overlap="rectangular">
					<ChatIcon />
				</Badge>
			</IconButton>
			<Popover
				disableScrollLock
				open={isOpen}
				anchorEl={anchorEl.current}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				classes={{ paper: classes.popoverPaper }}
				onClose={handleClickAway}
			>
				<List dense className={classes.tabContainer}>
                                    <ListItem>
                                        <div style={{ width: "100%" }}>
                                            <Button
                                                fullWidth
                                                color="primary"
                                                variant="contained"
                                                disabled={pushBusy || pushEnabled}
                                                onClick={handleEnablePush}
                                            >
                                                {pushBusy
                                                    ? "Activando..."
                                                    : pushEnabled
                                                        ? "Notificaciones activas"
                                                        : "Activar notificaciones"}
                                            </Button>
                                            <div
                                                role="status"
                                                style={{
                                                    marginTop: 8,
                                                    fontSize: 12
                                                }}
                                            >
                                                {pushInfo}
                                            </div>
                                        </div>
                                    </ListItem>

					{notifications.length === 0 ? (
						<ListItem>
							<ListItemText>{i18n.t("notifications.noTickets")}</ListItemText>
						</ListItem>
					) : (
						notifications.map(ticket => (
							<NotificationTicket key={ticket.id}>
								<TicketListItem ticket={ticket} />
							</NotificationTicket>
						))
					)}
				</List>
			</Popover>
		</>
	);
};

export default NotificationsPopOver;
